import React, { useContext, useEffect, useState } from 'react';

import { useMsal } from '@azure/msal-react';
import { useHistory, useLocation } from 'react-router';
import { LocationState } from '../../../../types/reactTypes';
import { pickAuthorizedSystem } from '../../../../utils/consumerUtil';
import { ClientConfigContext } from '../../../clientConfigContext';
import { AccessTokenStatus, useAccessToken } from '../../../hooks/useAccessToken';
import { useBrand } from '../../../hooks/useBrand';
import useCookie from '../../../hooks/useCookie';
import { useMyChartRedirect } from '../../../hooks/useMyChartRedirect';
import { QueryParamsStatus, useQueryParams } from '../../../hooks/useQueryParams';
import { useValidatedUrl } from '../../../hooks/useValidatedUrl';
import { ApiRequestLogger } from '../../../logger/apiRequestLogger';
import { getConsumer } from '../../../services/pims/pimsClient';
import { shouldShowIdentityVerificationPage } from '../../../util/sdkUtils';
import { redirectWithSoftLogout } from '../../../util/sessionTimeout';
import TransitionCard from '../../containers/TransitionCard';
import VerifyContactCustomerService from '../../containers/VerifyContactCustomerService';
import withLogger from '../../HOC/WithLogger';
import { AuthenticatedRoutes } from '../../routers';
import { CLEAR_SESSION_COOKIE_PREFIX } from '../verification/clear';
import IdentityVerificationPage from '../verification/identityVerificationPage/IdentityVerificationPage';
import VerifyEntry from '../verification/VerifyEntry';

type RegisterPageProps = { log: ApiRequestLogger };

enum RegisterPageState {
    Loading,
    Loaded,
}

type RegisterPageQueryParams = {
    brand: string;
    client_id?: string;
    correlation_id: string;
    cross_region?: string;
    ehr_system: string;
    redirect_uri: string;
    redirect_url?: string;
    register_phone?: string | boolean;
    sdk_version?: string;
    state?: string;
};

/**
 * This is the entry point into the MyChart authorization forms (authorization code and demographics).
 * This page is public and secured.
 *
 * Error conditions:
 * 1) We're not able to obtain a token for the logged-in user
 * 2) Missing request parameters, redirect_uri
 * 3) Corrupted state request parameter.
 * 4) The brand verification config is missing.
 * In the error scenarios, we display the Customer Service card.
 *
 * Otherwise, it obtains the end-user JWT and retrieves the PIMS consumer record.
 * If the consumer is MyChart authorized, then we redirect the end-user back to the redirection endpoint.
 * If the consumer is not authorized, or not authorized in the EHR system specified in the query, ehr_system (optional),
 * then we proceed to the register form.
 *
 * From other client-apps, register?brand=brand1&redirect_uri=<client_redirect_url>&ehr_system=&mobile=true
 * From other client-apps, register?brand=brand2&redirect_uri=<client_redirect_url2>&ehr_system=ehr1&mobile=true
 *
 * From Mychart app, register?brand=allProvidence&redirect_uri=SSO_url_for_noncross_region&cancel_redirect_uri=landing_page&ehrSystem=provwamt
 */
export const RegisterPage: React.FC<RegisterPageProps> = ({ log }): React.ReactElement => {
    const { status: accessTokenStatus, accessToken, error: accessTokenError } = useAccessToken();
    const { identityVerification, pimsApiUrl } = useContext(ClientConfigContext);
    const validateRedirectUrl = useValidatedUrl();
    const { getSamlSsoPage, isMyChartUrl } = useMyChartRedirect();
    const [pageState, setPageState] = useState<RegisterPageState>(RegisterPageState.Loading);
    const { status: queryParamStatus, queryParams, error: queryParamError } = useQueryParams<RegisterPageQueryParams>();
    const brand = useBrand();
    const { accounts, instance } = useMsal();
    const history = useHistory();
    const location = useLocation<LocationState>();

    // Show the new Identity Verification page if the user already has a clear session cookie or if the feature is enabled
    const azureUserId = accounts[0]?.localAccountId;
    const clearSessionCookieName = azureUserId ? `${CLEAR_SESSION_COOKIE_PREFIX}_${azureUserId}` : '';
    const [clearSessionId] = useCookie(clearSessionCookieName);

    // Determine if we skip ahead go to the register phone page
    let goToRegisterPhonePage = false;
    if (queryParams && queryParams.register_phone && !location.state?.showVerificationMethods) {
        goToRegisterPhonePage = typeof queryParams.register_phone === 'string' ? String(queryParams.register_phone).toLowerCase() === 'true' : queryParams.register_phone;
    }

    const redirectUrl = queryParams?.redirect_uri || queryParams?.redirect_url;

    /**
     * Cross-region logic is enabled if:
     * 1) The brand supports it (some brands like Kinwell do not support cross-region)
     * 2) The query parameter `cross_region` is set to 'true' indicating that this is a MyChart web redirect flow
     * 3) A redirect URL is provided
     * 4) The redirect URL is a MyChart URL
     */
    const isCrossRegionEnabled = brand.crossRegionEnabled && queryParams?.cross_region === 'true' && redirectUrl && isMyChartUrl(redirectUrl);

    useEffect(() => {
        /**
         * Skip this screen and proceed straight to the demographics flow
         * TODO: can this be improved to be less "goto" / spaghetti-code?
         */
        if (goToRegisterPhonePage) {
            history.push({
                pathname: AuthenticatedRoutes.VerificationRoutes.StartDemographicsFlow,
                // If we arrive here without this is_cross_region query param, we need to propagate it to the next page via the state.
                // TODO: Replace all this propagation with a React context
                state: { isCrossRegion },
            });
            return;
        }

        const getConsumerAsync = async (accessToken: string, redirectUrl: string) => {
            try {
                // based on the Brand, determine if the consumer has the authorization in the right EHR-System.
                // getConsumer accepts one EHR System. We enforce when the brand has only one accepted system.
                const enforceEhrSystem = brand.acceptedEhrSystems?.length === 1 ? brand.acceptedEhrSystems[0] : undefined;
                const consumer = await getConsumer(accessToken, pimsApiUrl, enforceEhrSystem);

                // base case 1: when the account is authorized, we redirect the patient out of the registration
                // case 2: otherwise, they stay on the registration workflow
                const authorizedEhrSystem = pickAuthorizedSystem(consumer, ehrSystem);
                if (authorizedEhrSystem) {
                    // already authorized. User should be redirected back to their redirect_uri
                    let finalRedirectUrl = redirectUrl;

                    // Apply cross-region logic.
                    if (isCrossRegionEnabled) {
                        finalRedirectUrl = getSamlSsoPage(authorizedEhrSystem);
                    }
                    redirectWithSoftLogout(instance, finalRedirectUrl, validateRedirectUrl);
                } else {
                    setPageState(RegisterPageState.Loaded);
                }
            } catch (err: any) {
                log.error(`getConsumerAsync error: ${err.message}. Proceed to registration`);
                setPageState(RegisterPageState.Loaded);
            }
        };

        // If the brand identity verification is undefined, we don't need to show the identity verification page, therefore the page should render with the old 2-button register screen
        if (accessToken && brand.identityVerification && redirectUrl) {
            getConsumerAsync(accessToken, redirectUrl);
        }
    }, [accessToken]);

    if (accessTokenStatus === AccessTokenStatus.Error) {
        return <VerifyContactCustomerService log={log} message={`Error getting access token: ${accessTokenError}`} />;
    }

    if (queryParamStatus === QueryParamsStatus.Error) {
        return <VerifyContactCustomerService log={log} message={`Error parsing query params: ${queryParamError}`} />;
    }

    if (!redirectUrl) {
        return <VerifyContactCustomerService log={log} message='Redirect URL is required' />;
    }

    if (!brand.identityVerification) {
        return <VerifyContactCustomerService log={log} message='No brand identity verification config found' />;
    }

    const azureClientId = queryParams.client_id;
    const ehrSystem = queryParams.ehr_system;
    const isCrossRegion = queryParams.cross_region === 'true' || location.state?.isCrossRegion === true; // Enables cross-region logic after the Prove/CLEAR authorization flows

    function showVerificationPage(): JSX.Element {
        /**
         * Show the 3-button Identity Verification page when an existing CLEAR session exists
         * OR the brand/client/sdk conditions are met
         */
        if (clearSessionId || shouldShowIdentityVerificationPage(azureClientId, brand)) {
            return <IdentityVerificationPage isCrossRegion={isCrossRegion} />;
        } else {
            return <VerifyEntry isCrossRegion={isCrossRegion} />;
        }
    }

    if (pageState === RegisterPageState.Loading) {
        return <TransitionCard />; // Silent sign-in in progress
    } else {
        return showVerificationPage();
    }
};

export default withLogger(RegisterPage, { eventSource: 'RegisterPage' });
